
import { useState, useEffect } from "react"; 
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { FirstTimePasswordChangeDialog } from "./FirstTimePasswordChangeDialog";


interface LoginFormProps {
  selectedModule: string;
}

const LoginForm = ({
  selectedModule
}: LoginFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAccessDeniedDialog, setShowAccessDeniedDialog] = useState(false);
  const [showLoginDisabledDialog, setShowLoginDisabledDialog] = useState(false);
  const [showMobileWarningDialog, setShowMobileWarningDialog] = useState(false);
  const [showLoginErrorDialog, setShowLoginErrorDialog] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [showFirstTimePasswordDialog, setShowFirstTimePasswordDialog] = useState(false);
  const [firstTimeUserEmail, setFirstTimeUserEmail] = useState("");
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        navigate('/');
        // Clear selected module when signed out
        localStorage.removeItem('selectedModule');
      } else if (event === 'TOKEN_REFRESHED') {
        if (!session) {
          await supabase.auth.signOut();
          toast.error("Session expired. Please sign in again.");
          navigate('/');
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const checkUserAccessAndNavigate = async (loginEmail: string) => {
    try {
      console.log("checkUserAccessAndNavigate called with email:", loginEmail);

      console.log("Fetching user record for:", loginEmail);
      // Read own profile via RLS (auth.uid() = id) using the user's own JWT
      const {
        data: users,
        error: usersError
      } = await supabase.from('users').select('*').eq('email', loginEmail);

      console.log("User query result:", { users, usersError });

      if (usersError) {
        console.error("Error checking user access:", usersError);
        toast.error("Failed to verify access rights. Please try again.");
        setLoading(false);
        return false;
      }

      // If no user record was found
      if (!users || users.length === 0) {
        console.error("User not found");
        setLoginErrorMessage("User profile not found. Please contact administrator.");
        setShowLoginErrorDialog(true);
        setLoading(false);
        return false;
      }

      // Get the first user record
      const user = users[0];
      console.log("Found user record:", user);

      // Check if user login is enabled
      if (!user.enable_login) {
        console.error("Login disabled for user:", loginEmail);
        setShowLoginDisabledDialog(true);
        
        // Sign the user out
        await supabase.auth.signOut();
        
        // Reset form state when login is disabled
        setLoading(false);
        setEmail("");
        setPassword("");
        return false;
      }

      // Check if this is the user's first time login (last_login is null)
      console.log("Checking last_login:", user.last_login);
      if (user.last_login === null) {
        console.log("First time login detected for user:", loginEmail);
        console.log("Setting first-time dialog states...");
        setIsFirstTimeUser(true);
        setFirstTimeUserEmail(loginEmail);
        setShowFirstTimePasswordDialog(true);
        console.log("Dialog states set - isFirstTimeUser:", true, "firstTimeUserEmail:", loginEmail, "showDialog:", true);
        setLoading(false);
        return false; // Don't proceed to navigation yet
      }

      // Update last_login via SECURITY DEFINER RPC (caller-only)
      await supabase.rpc('mark_last_login');

      // Check if user has access to the selected module
      let hasAccess = false;
      switch (selectedModule) {
        case 'administration':
          hasAccess = user.is_admin && user.access_administration;
          break;
        case 'talent-acquisition':
        case 'hr':
          hasAccess = user.access_hr;
          break;
        case 'sales':
          hasAccess = user.access_sales;
          break;
        case 'marketing':
          hasAccess = user.access_marketing;
          break;
        case 'finance':
          hasAccess = user.access_finance;
          break;
        case 'legal':
          hasAccess = user.access_legal;
          break;
        case 'projects':
          hasAccess = user.access_projects;
          break;
        case 'performance':
          hasAccess = user.access_performance;
          break;
        default:
          hasAccess = false;
      }

      if (hasAccess) {
        handleModuleNavigation();
        setLoading(false);
        return true;
      } else {
        // Show the access denied dialog
        setShowAccessDeniedDialog(true);

        // Sign the user out
        await supabase.auth.signOut();

        // Reset form state when access is denied
        setLoading(false);
        setEmail("");
        setPassword("");
        return false;
      }
    } catch (error: any) {
      console.error("Error in checkUserAccessAndNavigate:", error);
      toast.error("Failed to verify access rights. Please try again.");
      setLoading(false);
      return false;
    }
  };

  const handleModuleNavigation = () => {
    // Store the selected module in localStorage before navigating
    localStorage.setItem('selectedModule', selectedModule);
    // Store first-time user status for the target module to handle
    if (isFirstTimeUser) {
      localStorage.setItem('isFirstTimeUser', 'true');
    }
    switch (selectedModule) {
      case 'administration':
        navigate('/administration');
        break;
      case 'talent-acquisition':
      case 'hr':
        navigate('/dashboard');
        break;
      case 'sales':
        navigate('/sales');
        break;
      case 'marketing':
        navigate('/marketing');
        break;
      case 'legal':
        navigate('/legal');
        break;
      case 'finance':
        navigate('/finance');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'performance':
        navigate('/performance');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }
    try {
      const {
        error
      } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });
      if (error) throw error;
      toast.success("Confirmation email resent! Please check your inbox.");
    } catch (error: any) {
      console.error("Error resending confirmation:", error);
      toast.error("Failed to resend confirmation email. Please try again.");
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if user is on a mobile device
    if (isMobile) {
      setShowMobileWarningDialog(true);
      return;
    }
    setLoading(true);
    
    try {
      // Authenticate FIRST (no pre-auth user lookup → prevents enumeration)
      const {
        data: { user },
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        setPassword(""); // Clear password on error for security

        if (error.message?.includes("Invalid login credentials")) {
          setLoginErrorMessage("The email or password you entered is incorrect. If login continues to fail despite using the correct password, please contact your administrator.");
          setShowLoginErrorDialog(true);
        } else if (error.message?.includes("Email not confirmed")) {
          toast.error(<div className="space-y-2">
              <p>Please confirm your email before signing in.</p>
              <button onClick={handleResendConfirmation} className="text-primary hover:underline">
                Resend confirmation email
              </button>
            </div>, {
            duration: 10000
          });
        } else {
          setLoginErrorMessage(error.message || "An error occurred during sign in");
          setShowLoginErrorDialog(true);
        }
        setLoading(false);
        return;
      }

      // User successfully authenticated
      toast.success("Successfully signed in!");

      // Proceed with normal login flow
      const result = await checkUserAccessAndNavigate(email);
    } catch (error: any) {
      console.error("Login error:", error);
      setLoginErrorMessage("An error occurred during sign in. Please try again.");
      setShowLoginErrorDialog(true);
      setLoading(false);
    }
  };

  // Handle dialog dismiss - ensure form is reset
  const handleDialogDismiss = () => {
    setShowAccessDeniedDialog(false);
    setLoading(false);
    setEmail("");
    setPassword("");
  };

  // Handle login disabled dialog dismiss
  const handleLoginDisabledDismiss = () => {
    setShowLoginDisabledDialog(false);
    setLoading(false);
    setEmail("");
    setPassword("");
  };

  // Handle mobile warning dialog dismiss
  const handleMobileWarningDismiss = () => {
    setShowMobileWarningDialog(false);
  };

  // Handle login error dialog dismiss
  const handleLoginErrorDismiss = () => {
    setShowLoginErrorDialog(false);
    setLoading(false);
  };

  // Handle first-time password change success
  const handleFirstTimePasswordSuccess = async () => {
    setShowFirstTimePasswordDialog(false);
    setIsFirstTimeUser(false);
    setFirstTimeUserEmail("");
    // Log out the user so they can login with their new password
    await supabase.auth.signOut();
    setEmail("");
    setPassword("");
    toast.success("Password changed successfully. Please login again with your new password.");
  };

  // Handle first-time password change cancel
  const handleFirstTimePasswordCancel = async () => {
    setShowFirstTimePasswordDialog(false);
    setIsFirstTimeUser(false);
    setFirstTimeUserEmail("");
    // Log out the user and reset form
    await supabase.auth.signOut();
    setEmail("");
    setPassword("");
    setLoading(false);
  };


  return <>
      <form onSubmit={handleSignIn} className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center">
            <label className="text-indigo-100 w-[90px]">User name:</label>
            <Input type="email" placeholder="Provide user name" value={email} onChange={e => setEmail(e.target.value)} required disabled={loading} className="border-gray-800 text-white bg-gray-900 max-w-xs w-[230px]" />
          </div>
          <div className="flex items-center">
            <label className="text-indigo-100 w-[90px]">Password:</label>
            <Input type="password" placeholder="Provide password" value={password} onChange={e => setPassword(e.target.value)} required disabled={loading} className="border-gray-800 text-white bg-gray-900 max-w-xs w-[230px]" />
          </div>
        </div>
        <div className="flex justify-start">
          <Button type="submit" disabled={loading} size="sm" className="bg-primary hover:bg-primary/90 w-28">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </div>
      </form>

      {/* Access Denied Dialog */}
      <AlertDialog open={showAccessDeniedDialog} onOpenChange={setShowAccessDeniedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Access Denied</AlertDialogTitle>
            <AlertDialogDescription>
              You do not have access to the selected application module. Check with the administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleDialogDismiss} className="w-[100px] item-center justify-center">OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Login Disabled Dialog */}
      <AlertDialog open={showLoginDisabledDialog} onOpenChange={setShowLoginDisabledDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Login Disabled</AlertDialogTitle>
            <AlertDialogDescription>
              Login disabled. Please check with the administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleLoginDisabledDismiss} className="w-[100px] item-center justify-center">OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Mobile Warning Dialog */}
      <AlertDialog open={showMobileWarningDialog} onOpenChange={setShowMobileWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Device Not Supported</AlertDialogTitle>
            <AlertDialogDescription>
              The application is not designed for viewing on a phone. Kindly access from a device with a larger screen resolution.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleMobileWarningDismiss}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Login Error Dialog */}
      <AlertDialog open={showLoginErrorDialog} onOpenChange={setShowLoginErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Login Error</AlertDialogTitle>
            <AlertDialogDescription>
              {loginErrorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleLoginErrorDismiss}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* First Time Password Change Dialog */}
      <FirstTimePasswordChangeDialog
        isOpen={showFirstTimePasswordDialog}
        onSuccess={handleFirstTimePasswordSuccess}
        onCancel={handleFirstTimePasswordCancel}
        userEmail={firstTimeUserEmail}
      />
      
    </>;
};

export default LoginForm;
